/**
 * HomePage.tsx
 *
 * Default landing dashboard — a unified overview across the account:
 *   1. Needs Your Attention — pending approval gates (all runs), polled 30s
 *   2. Active Runs          — runs currently in progress
 *   3. Recent Activity      — last 10 completed/failed runs
 *   4. Scraping Health      — compact 7-day success rate
 *
 * Clicking any run navigates to it in Mission Control (onOpenRun → DAG Canvas).
 *
 * Data notes (no backend changes):
 *   • Approvals carry no agent name, so agent labels are best-effort: mapped
 *     from the runs we fetch (by runId), falling back to a short run ID.
 *   • "Current step" is not in the runs list, so Active Runs shows start time
 *     + status rather than a per-run steps fetch.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { AlertCircle, CheckCircle2, Activity, Clock, ShieldCheck, ChevronRight } from 'lucide-react'

import { api } from '../api/client'
import { GlassPanel } from './common/GlassPanel'
import { StatusBadge } from './common/StatusBadge'

const POLL_MS = 30_000

// ── Types (subset of the API shapes we read) ──────────────────────────────────

interface RunRow {
  id: string
  agentId?: string
  agentName?: string
  status: string
  startedAt?: string | null
  finishedAt?: string | null
}

interface ApprovalRow {
  id: string
  runId: string
  stepId?: string
  skillId?: string
  requestedAt: string
}

interface HealthSummary {
  success_rate: number
  ok: number
  blocked: number
  error: number
}

// ── Formatting helpers ──────────────────────────────────────────────────────

function shortId(id?: string): string {
  if (!id) return '—'
  return id.length > 10 ? `${id.slice(0, 8)}…` : id
}

function fmtAgo(iso?: string | null): string {
  if (!iso) return '—'
  const ms = Date.now() - new Date(iso).getTime()
  if (isNaN(ms)) return '—'
  const s = Math.max(0, Math.floor(ms / 1000))
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ${m % 60}m`
  return `${Math.floor(h / 24)}d`
}

function fmtDuration(startedAt?: string | null, finishedAt?: string | null): string {
  if (!startedAt || !finishedAt) return '—'
  const ms = new Date(finishedAt).getTime() - new Date(startedAt).getTime()
  if (isNaN(ms) || ms < 0) return '—'
  if (ms < 1000) return `${ms}ms`
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`
  return `${Math.floor(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`
}

function fmtTime(iso?: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
    })
  } catch {
    return iso
  }
}

// Normalise the runs list response ({items}|{runs}|array) → RunRow[].
function extractRuns(json: unknown): RunRow[] {
  if (Array.isArray(json)) return json as RunRow[]
  const o = json as { items?: RunRow[]; runs?: RunRow[] } | null
  return (o?.items ?? o?.runs ?? []) as RunRow[]
}

// ── Section shell ─────────────────────────────────────────────────────────────

function SectionHeader({ icon, title, accent }: { icon: React.ReactNode; title: string; accent: string }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <span style={{ color: accent }} aria-hidden="true">{icon}</span>
      <h2 className="text-[14px] font-[900] text-[#dce9f7] m-0 tracking-[-0.02em]">{title}</h2>
    </div>
  )
}

// ── Page ───────────────────────────────────────────────────────────────────

export interface HomePageProps {
  tenantId?: string
  onOpenRun: (runId: string) => void
  onOpenHealth: () => void
}

export function HomePage({ tenantId, onOpenRun, onOpenHealth }: HomePageProps) {
  const [approvals, setApprovals] = useState<ApprovalRow[]>([])
  const [active, setActive] = useState<RunRow[]>([])
  const [recent, setRecent] = useState<RunRow[]>([])
  const [health, setHealth] = useState<HealthSummary | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // runId → agent name, built from whatever runs we've fetched (best-effort
  // labelling for approval cards, which carry no agent name).
  const agentByRun = useRef<Map<string, string>>(new Map())

  const refresh = useCallback(async () => {
    try {
      const [approvalsRes, activeRes, recentRes, healthRes] = await Promise.allSettled([
        api.get<{ items?: ApprovalRow[] }>('/v1/approvals', { params: { status: 'pending', limit: 50 } }),
        api.get<unknown>('/v1/runs', { params: { status: 'running', pageSize: 50 } }),
        api.get<unknown>('/v1/runs', { params: { status: 'succeeded,failed,cancelled,timed_out', pageSize: 10 } }),
        api.get<{ summary?: HealthSummary }>('/v1/analytics/scraping-health', { params: { days: 7 } }),
      ])

      const activeRuns = activeRes.status === 'fulfilled' ? extractRuns(activeRes.value) : []
      const recentRuns = recentRes.status === 'fulfilled' ? extractRuns(recentRes.value) : []

      // Refresh the runId → agentName map from every run we know about.
      for (const r of [...activeRuns, ...recentRuns]) {
        if (r.agentName) agentByRun.current.set(r.id, r.agentName)
      }

      if (activeRes.status === 'fulfilled') setActive(activeRuns)
      if (recentRes.status === 'fulfilled') setRecent(recentRuns.slice(0, 10))
      if (approvalsRes.status === 'fulfilled') setApprovals(approvalsRes.value.items ?? [])
      if (healthRes.status === 'fulfilled') setHealth(healthRes.value.summary ?? null)

      // Only surface an error if everything failed (otherwise show partial data).
      const allFailed = [approvalsRes, activeRes, recentRes, healthRes].every((p) => p.status === 'rejected')
      setError(allFailed ? 'Failed to load dashboard' : null)
    } catch {
      setError('Failed to load dashboard')
    } finally {
      setLoaded(true)
    }
  }, [])

  useEffect(() => {
    void refresh()
    const t = setInterval(() => void refresh(), POLL_MS)
    return () => clearInterval(t)
  }, [refresh, tenantId])

  const agentLabel = (runId: string, agentId?: string, agentName?: string) =>
    agentName ?? agentByRun.current.get(runId) ?? shortId(agentId ?? runId)

  return (
    <div className="pt-2 pb-12 max-w-[1000px] flex flex-col gap-7">
      {error && (
        <div className="text-[12px] text-[#f87171] px-4 py-3 bg-[rgba(239,68,68,0.08)] border border-[rgba(239,68,68,0.2)] rounded-xl">
          {error}{' '}
          <button className="underline bg-transparent border-none text-[#f87171] cursor-pointer p-0" onClick={() => void refresh()}>Retry</button>
        </div>
      )}

      {/* ── Section 1: Needs Your Attention ── */}
      <section>
        <SectionHeader icon={<AlertCircle size={16} />} title="Needs Your Attention" accent="#f59e0b" />
        {!loaded ? (
          <div className="h-[72px] rounded-xl border border-[rgba(148,163,184,0.1)] bg-[rgba(11,18,32,0.4)] animate-pulse" />
        ) : approvals.length === 0 ? (
          <div className="inline-flex items-center gap-2 text-[12.5px] font-semibold text-[#4ade80] bg-[rgba(34,197,94,0.1)] border border-[rgba(34,197,94,0.28)] rounded-[10px] px-[14px] py-[10px]">
            <CheckCircle2 size={15} /> All clear — no pending approvals
          </div>
        ) : (
          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
            {approvals.map((a) => (
              <GlassPanel key={a.id} glow="warning" className="p-4 flex flex-col gap-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[13px] font-bold text-[#dce9f7] truncate">{agentLabel(a.runId)}</div>
                    <div className="text-[10.5px] text-[#4e6480] font-mono truncate">run {shortId(a.runId)}</div>
                  </div>
                  <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-[#fbbf24] whitespace-nowrap flex-shrink-0">
                    <Clock size={11} /> {fmtAgo(a.requestedAt)}
                  </span>
                </div>
                <div className="text-[11.5px] text-[#8fa3bd]">
                  Waiting on <span className="text-[#c8d9ee] font-semibold">{a.skillId || a.stepId || 'approval'}</span>
                </div>
                <button
                  type="button"
                  onClick={() => onOpenRun(a.runId)}
                  className="self-start mt-1 inline-flex items-center gap-[6px] px-[12px] py-[7px] rounded-[8px] border border-[rgba(245,158,11,0.45)] bg-[rgba(245,158,11,0.14)] text-[#fbbf24] text-[11px] font-[800] cursor-pointer transition-colors hover:bg-[rgba(245,158,11,0.22)]"
                >
                  Review <ChevronRight size={13} />
                </button>
              </GlassPanel>
            ))}
          </div>
        )}
      </section>

      {/* ── Section 2: Active Runs ── */}
      <section>
        <SectionHeader icon={<Activity size={16} />} title="Active Runs" accent="#3b82f6" />
        {!loaded ? (
          <div className="h-[56px] rounded-xl border border-[rgba(148,163,184,0.1)] bg-[rgba(11,18,32,0.4)] animate-pulse" />
        ) : active.length === 0 ? (
          <p className="text-[12.5px] text-[#4e6480] m-0">No runs in progress.</p>
        ) : (
          <div className="flex flex-col gap-2">
            {active.map((r) => (
              <GlassPanel
                key={r.id}
                className="px-4 py-[12px] flex items-center gap-4 cursor-pointer transition-colors hover:border-[rgba(56,189,248,0.3)]"
                onClick={() => onOpenRun(r.id)}
                role="button"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-bold text-[#dce9f7] truncate">{agentLabel(r.id, r.agentId, r.agentName)}</div>
                  <div className="text-[10.5px] text-[#4e6480] font-mono truncate mt-0.5">run {shortId(r.id)} · started {fmtAgo(r.startedAt)} ago</div>
                </div>
                <StatusBadge status={r.status} />
                <ChevronRight size={15} className="text-[#3a5068] flex-shrink-0" />
              </GlassPanel>
            ))}
          </div>
        )}
      </section>

      {/* ── Section 3: Recent Activity ── */}
      <section>
        <SectionHeader icon={<Clock size={16} />} title="Recent Activity" accent="#94a3b8" />
        {!loaded ? (
          <div className="h-[56px] rounded-xl border border-[rgba(148,163,184,0.1)] bg-[rgba(11,18,32,0.4)] animate-pulse" />
        ) : recent.length === 0 ? (
          <p className="text-[12.5px] text-[#4e6480] m-0">No recent runs.</p>
        ) : (
          <div className="flex flex-col gap-2">
            {recent.map((r) => (
              <GlassPanel
                key={r.id}
                className="px-4 py-[10px] flex items-center gap-4 cursor-pointer transition-colors hover:border-[rgba(56,189,248,0.3)]"
                onClick={() => onOpenRun(r.id)}
                role="button"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] font-semibold text-[#c8d9ee] truncate">{agentLabel(r.id, r.agentId, r.agentName)}</div>
                  <div className="text-[10.5px] text-[#4e6480] font-mono truncate mt-0.5">
                    {fmtTime(r.finishedAt)} · {fmtDuration(r.startedAt, r.finishedAt)}
                  </div>
                </div>
                <StatusBadge status={r.status} />
                <ChevronRight size={15} className="text-[#3a5068] flex-shrink-0" />
              </GlassPanel>
            ))}
          </div>
        )}
      </section>

      {/* ── Section 4: Scraping Health (compact) ── */}
      <section>
        <SectionHeader icon={<ShieldCheck size={16} />} title="Scraping Health" accent="#34d399" />
        <GlassPanel className="p-5 flex items-center gap-8 flex-wrap">
          <div className="flex flex-col">
            <span className="text-[9.5px] font-[800] tracking-[0.12em] uppercase text-[#3a5068]">7-day success</span>
            <span className="text-[28px] font-[900] text-[#34d399] leading-none tabular-nums mt-1">
              {health ? `${Math.round(health.success_rate * 100)}%` : '—'}
            </span>
          </div>
          <div className="flex items-center gap-6">
            <div className="flex flex-col"><span className="text-[9.5px] font-[800] tracking-[0.1em] uppercase text-[#3a5068]">OK</span><span className="text-[16px] font-bold text-[#4ade80] tabular-nums">{health?.ok ?? '—'}</span></div>
            <div className="flex flex-col"><span className="text-[9.5px] font-[800] tracking-[0.1em] uppercase text-[#3a5068]">Blocked</span><span className="text-[16px] font-bold text-[#fbbf24] tabular-nums">{health?.blocked ?? '—'}</span></div>
            <div className="flex flex-col"><span className="text-[9.5px] font-[800] tracking-[0.1em] uppercase text-[#3a5068]">Errors</span><span className="text-[16px] font-bold text-[#f87171] tabular-nums">{health?.error ?? '—'}</span></div>
          </div>
          <button
            type="button"
            onClick={onOpenHealth}
            className="ml-auto inline-flex items-center gap-[6px] px-[12px] py-[7px] rounded-[8px] border border-[rgba(148,163,184,0.2)] bg-[rgba(148,163,184,0.06)] text-[#c8d9ee] text-[11px] font-bold cursor-pointer transition-colors hover:bg-[rgba(148,163,184,0.12)]"
          >
            View details <ChevronRight size={13} />
          </button>
        </GlassPanel>
      </section>
    </div>
  )
}
